import { useState } from "react";

import { useDebouncedValue } from "../../../../hooks/useDebouncedValue";
import {
  AdminActivityArgs,
  AdminActivityPageSize,
  AdminActivityRecord,
  useGetAdminActivityQuery,
} from "../../../../services";
import { toSectionState } from "../../utils/sectionState";
import {
  ACTIVITY_SEARCH_DEBOUNCE_MS,
  ACTIVITY_SEARCH_MAX_LENGTH,
  DEFAULT_ACTIVITY_PAGE_SIZE,
} from "../constants";
import { ActivityActionFilter, ActivityPeriodFilter } from "../types";

const NO_RECORDS: AdminActivityRecord[] = [];

export const useActivity = () => {
  const [query, setQuery] = useState("");
  const [action, setAction] = useState<ActivityActionFilter>("all");
  const [period, setPeriod] = useState<ActivityPeriodFilter>("all");
  const [pageSize, setPageSize] = useState<AdminActivityPageSize>(
    DEFAULT_ACTIVITY_PAGE_SIZE,
  );

  const debouncedQuery = useDebouncedValue(
    query.trim(),
    ACTIVITY_SEARCH_DEBOUNCE_MS,
  );
  const filters = {
    ...(debouncedQuery && { query: debouncedQuery }),
    ...(action !== "all" && { action }),
    ...(period !== "all" && { period }),
  };
  // A page belongs to the filters it was picked under, so any filter or size change starts again at page 0.
  const filterKey = JSON.stringify({ ...filters, pageSize });
  const [paging, setPaging] = useState({ key: filterKey, page: 0 });
  const page = paging.key === filterKey ? paging.page : 0;

  const args: AdminActivityArgs = { ...filters, page, limit: pageSize };
  const activityQuery = useGetAdminActivityQuery(args, {
    refetchOnMountOrArgChange: true,
  });

  // currentData, so a failed read after a filter change never shows the previous filter's rows.
  const section = toSectionState({
    data: activityQuery.currentData,
    isError: activityQuery.isError,
  });
  const records =
    section.state === "available" ? section.data.records : NO_RECORDS;
  const total = section.state === "available" ? section.data.total : 0;

  const changeQuery = (next: string) =>
    setQuery(next.slice(0, ACTIVITY_SEARCH_MAX_LENGTH));
  const changePage = (next: number) =>
    setPaging({ key: filterKey, page: next });
  const clearFilters = () => {
    setQuery("");
    setAction("all");
    setPeriod("all");
  };

  return {
    section,
    query,
    action,
    period,
    page,
    pageSize,
    records,
    total,
    isFiltered: query.trim() !== "" || action !== "all" || period !== "all",
    changeQuery,
    changeAction: setAction,
    changePeriod: setPeriod,
    changePage,
    changePageSize: setPageSize,
    clearFilters,
    isRefreshing: activityQuery.isFetching,
    refresh: () => void activityQuery.refetch(),
  };
};
